import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Clock,
  Target,
  Zap,
  CalendarCheck,
  ChevronDown,
  Info,
} from 'lucide-react';
import {
  fetchEmployeeMyPerformance,
  getTaskAnalyticsErrorMessage,
  pickFiltersApplied,
} from '../../services/taskAnalyticsApi';
import LoadingSpinner from '../common/LoadingSpinner';

const FREQUENCY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const TASK_FREQUENCY_LABEL = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function EmployeeFiltersLine({ payload }) {
  const f = pickFiltersApplied(payload);
  if (!f || typeof f !== 'object') return null;
  const c = f.cadence;
  const parts = [`Frequency: ${c == null || c === '' ? 'all' : c}`];
  return <p className="text-xs text-gray-500 mt-2">Active filters (API): {parts.join(' · ')}</p>;
}

const MyPerformance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cadenceFilter, setCadenceFilter] = useState('');

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (cadenceFilter) params.set('cadence', cadenceFilter);
      const payload = await fetchEmployeeMyPerformance(params);
      setData(payload);
    } catch (err) {
      console.error('Error fetching performance:', err);
      setError(getTaskAnalyticsErrorMessage(err, 'Could not load your performance data.'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cadenceFilter]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-600 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-red-900">Something went wrong</h2>
        <p className="text-sm text-red-800 mt-2">{error}</p>
        <button
          type="button"
          onClick={fetchPerformance}
          className="mt-4 px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p className="text-lg font-medium">No performance data available</p>
        <p className="text-sm mt-1">Complete some tasks to see your performance metrics.</p>
      </div>
    );
  }

  const statusMap = (data.statusCounts || []).reduce((acc, item) => {
    acc[item._id] = item.count;
    return acc;
  }, {});

  const cadenceBreakdown = data.cadenceBreakdown || [];
  const monthlyTrend = data.monthlyTrend || [];

  const completionRateColor = (data.overallCompletionRate || 0) >= 70
    ? 'text-emerald-600'
    : (data.overallCompletionRate || 0) >= 40
      ? 'text-amber-600'
      : 'text-red-600';

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-indigo-600" />
            My performance
          </h2>
          <p className="text-gray-600 mt-1">Your tasks, completion rate, and recent activity</p>
          <EmployeeFiltersLine payload={data} />
        </div>
        <label className="flex flex-col text-sm font-medium text-gray-700 min-w-[180px]">
          Filter by frequency
          <div className="relative mt-1">
            <select
              value={cadenceFilter}
              onChange={(e) => setCadenceFilter(e.target.value)}
              className="appearance-none w-full pl-3 pr-9 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
            >
              {FREQUENCY_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </label>
      </div>

      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 flex gap-3">
        <Info className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-indigo-950">
          <p className="font-medium">How to read this page</p>
          <p className="mt-1 text-indigo-900/90">
            <strong>Completion rate</strong> is finished tasks divided by all tasks assigned to you (for the filter
            above). <strong>Frequency</strong> means how often your tasks are meant to be checked (daily, weekly, monthly,
            or yearly).
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-shrink-0">
            <div className="relative w-32 h-32 w-full">
              <svg className="w-32 h-32 transform -rotate-90 w-full" viewBox="0 0 120 120" aria-hidden>
                <circle cx="60" cy="60" r="50" stroke="#e5e7eb" strokeWidth="10" fill="none" />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  stroke={
                    (data.overallCompletionRate || 0) >= 70
                      ? '#10b981'
                      : (data.overallCompletionRate || 0) >= 40
                        ? '#f59e0b'
                        : '#ef4444'
                  }
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${((data.overallCompletionRate || 0) / 100) * 314} 314`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-bold ${completionRateColor}`}>
                  {(data.overallCompletionRate || 0).toFixed(0)}%
                </span>
                <span className="text-xs text-gray-500">done</span>
              </div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MiniStat label="Total tasks" value={data.totalTasks || 0} icon={Target} />
            <MiniStat label="This week" value={data.completedThisWeek || 0} icon={Zap} color="sky" />
            <MiniStat label="This month" value={data.completedThisMonth || 0} icon={CalendarCheck} color="indigo" />
            <MiniStat label="Overdue" value={data.overdueTasks || 0} icon={AlertTriangle} color="red" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CheckCircle} label="Completed" value={statusMap.completed || 0} color="emerald" />
        <StatCard icon={Clock} label="In progress" value={statusMap['in-progress'] || 0} color="sky" />
        <StatCard icon={AlertTriangle} label="Overdue" value={data.overdueTasks || 0} color="red" />
        <StatCard
          icon={TrendingUp}
          label="Avg. days to finish"
          value={
            data.avgCompletionDays != null && Number.isFinite(Number(data.avgCompletionDays))
              ? `${Number(data.avgCompletionDays).toFixed(1)} days`
              : 'N/A'
          }
          color="amber"
        />
      </div>

      {cadenceBreakdown.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-base font-semibold text-gray-900">Your tasks by frequency</h3>
            <p className="text-sm text-gray-600 mt-1">Each row: how many you finished out of how many assigned.</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {cadenceBreakdown.map((row) => {
              const total = row.totalTasks || 0;
              const done = row.completedTasks || 0;
              const prog = row.inProgressTasks || 0;
              const other = Math.max(0, total - done - prog);
              const pct =
                row.completionRate != null
                  ? Number(row.completionRate).toFixed(0)
                  : total
                    ? ((done / total) * 100).toFixed(0)
                    : '0';
              const label = TASK_FREQUENCY_LABEL[row.cadence] || row.cadence;
              return (
                <li key={row.cadence} className="px-5 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="font-medium text-gray-900">{label}</span>
                    <span className="text-sm text-gray-600 sm:text-right">
                      <span className="font-semibold text-gray-900">{done}</span> completed
                      {total > 0 && (
                        <>
                          {' '}
                          of <span className="font-semibold text-gray-900">{total}</span> assigned
                        </>
                      )}
                      {' · '}
                      <span className="font-semibold text-indigo-700">{pct}%</span> done
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Still active: {prog} in progress
                    {other > 0 ? `, ${other} not completed yet` : ''}.
                  </p>
                  <div className="mt-2 h-2 w-full rounded-full bg-gray-100 overflow-hidden" aria-hidden>
                    <div
                      className="h-full rounded-full bg-indigo-600 transition-all"
                      style={{ width: `${Math.min(100, Number(pct) || 0)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {monthlyTrend.length > 0 && (
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-base font-semibold text-gray-900">Tasks you completed by month</h3>
            <p className="text-sm text-gray-600 mt-1">Recent months (from the server). Larger numbers mean more tasks finished that month.</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {monthlyTrend.map((item, i) => {
              const label = `${MONTH_NAMES[(item.month || 1) - 1]} ${item.year}`;
              const count = item.completed || 0;
              const max = Math.max(...monthlyTrend.map((m) => m.completed || 0), 1);
              const w = (count / max) * 100;
              return (
                <li key={`${item.year}-${item.month}-${i}`} className="px-5 py-3 flex items-center gap-4">
                  <span className="w-24 text-sm font-medium text-gray-700 flex-shrink-0">{label}</span>
                  <div className="flex-1 flex items-center gap-3 min-w-0">
                    <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-600" style={{ width: `${w}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-8 text-right">{count}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Status breakdown</h3>
        <p className="text-sm text-gray-500 mb-4">Where your tasks sit in the workflow.</p>
        <div className="space-y-3">
          {[
            { key: 'assigned', label: 'Assigned', color: 'bg-blue-500' },
            { key: 'in-progress', label: 'In progress', color: 'bg-sky-500' },
            { key: 'review-pending', label: 'Review pending', color: 'bg-amber-500' },
            { key: 'rejected', label: 'Sent back (review)', color: 'bg-red-500' },
            { key: 'completed', label: 'Completed', color: 'bg-emerald-500' },
          ].map(({ key, label, color }) => {
            const count = statusMap[key] || 0;
            const total = data.totalTasks || 1;
            const pct = ((count / total) * 100).toFixed(0);
            return (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <span className="text-sm text-gray-600">
                    {count} ({pct}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: 'text-indigo-500' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'text-emerald-500' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-600', icon: 'text-sky-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'text-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', icon: 'text-red-500' },
  };
  const c = colors[color] || colors.indigo;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${c.text}`}>{value}</p>
      </div>
      <div className={`p-2.5 rounded-lg ${c.bg}`}>
        <Icon className={`h-6 w-6 ${c.icon}`} />
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, icon: Icon, color = 'emerald' }) => {
  const colors = {
    emerald: 'text-emerald-600',
    sky: 'text-sky-600',
    indigo: 'text-indigo-600',
    red: 'text-red-600',
  };
  return (
    <div className="text-center">
      <Icon className={`h-5 w-5 mx-auto mb-1 ${colors[color] || 'text-gray-500'}`} />
      <p className={`text-xl font-bold ${colors[color] || 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

export default MyPerformance;
